import { apiRequest } from '../../../shared/api';
import {
  Inspection,
  InspectionPlan,
  InspectionChecklistTemplate,
  InspectionChecklistInstance,
  InspectionChecklistResponse,
  InspectionFindingLink,
  InspectionReport,
  InspectionDashboardStats,
  InspectionStatus,
  InspectionType,
  OverallResult,
  ChecklistResponseStatus,
  FindingSeverity,
} from './types';

export const inspectionService = {
  // Dashboard
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<InspectionDashboardStats>(`/inspections/analytics/dashboard?organizationId=${organizationId}`),

  // Plans
  getPlans: (params: {
    organizationId?: string;
    planType?: string;
    status?: string;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.planType) query.append('planType', params.planType);
    if (params.status) query.append('status', params.status);
    return apiRequest<InspectionPlan[]>(`/inspections/plans?${query.toString()}`);
  },

  createPlan: (data: {
    organizationId?: string;
    name: string;
    description?: string;
    planType: string;
    periodStart: string;
    periodEnd: string;
    frequency?: string;
    ownerId: string;
    riskBased?: boolean;
    approvedBy?: string;
  }) => {
    return apiRequest<InspectionPlan>('/inspections/plans', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  // Checklists
  getChecklistTemplates: (
    organizationId: string = 'ORG-XYZ',
    inspectionType?: InspectionType
  ) => {
    const query = new URLSearchParams();
    query.append('organizationId', organizationId);
    if (inspectionType) query.append('inspectionType', inspectionType);
    return apiRequest<InspectionChecklistTemplate[]>(`/inspections/checklists/templates?${query.toString()}`);
  },

  createChecklistInstance: (inspectionId: string, templateId: string) => {
    return apiRequest<InspectionChecklistInstance>(`/inspections/${inspectionId}/checklists`, {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    });
  },

  recordResponse: (
    instanceId: string,
    data: {
      questionId: string;
      responseStatus: ChecklistResponseStatus;
      notes?: string;
    }
  ) => {
    return apiRequest<InspectionChecklistResponse>(`/inspections/checklists/${instanceId}/responses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Inspections Register
  getInspections: (params: {
    organizationId?: string;
    status?: InspectionStatus;
    inspectionType?: InspectionType;
    department?: string;
    assetReference?: string;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.status) query.append('status', params.status);
    if (params.inspectionType) query.append('inspectionType', params.inspectionType);
    if (params.department) query.append('department', params.department);
    if (params.assetReference) query.append('assetReference', params.assetReference);
    return apiRequest<Inspection[]>(`/inspections?${query.toString()}`);
  },

  getInspectionById: (id: string) => apiRequest<Inspection>(`/inspections/${id}`),

  createInspection: (data: {
    organizationId?: string;
    planId?: string;
    title: string;
    inspectionType: InspectionType;
    priority?: string;
    department: string;
    location?: string;
    assetReference?: string;
    flightNumber?: string;
    scheduledDate: string;
    leadInspectorId: string;
    leadInspectorName: string;
    notes?: string;
    createdBy: string;
  }) => {
    return apiRequest<Inspection>('/inspections', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  startInspection: (id: string, actor: string = 'INSPECTOR') => {
    return apiRequest<Inspection>(`/inspections/${id}/start`, {
      method: 'POST',
      body: JSON.stringify({ actor }),
    });
  },

  applyCriticalContainment: (id: string, notes: string, actor: string = 'INSPECTOR') => {
    return apiRequest<Inspection>(`/inspections/${id}/critical-containment`, {
      method: 'POST',
      body: JSON.stringify({ notes, actor }),
    });
  },

  completeInspection: (
    id: string,
    overallResult: OverallResult = 'SATISFACTORY',
    actor: string = 'INSPECTOR'
  ) => {
    return apiRequest<Inspection>(`/inspections/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ overallResult, actor }),
    });
  },

  closeInspection: (id: string, closedBy: string = 'QUALITY_SUPERVISOR') => {
    return apiRequest<Inspection>(`/inspections/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ closedBy }),
    });
  },

  createFinding: (
    inspectionId: string,
    data: {
      responseId?: string;
      severity: FindingSeverity;
      title: string;
      description: string;
      containmentAction?: string;
      targetDate: string;
    }
  ) => {
    return apiRequest<InspectionFindingLink>(`/inspections/${inspectionId}/findings`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createReport: (
    inspectionId: string,
    data: {
      summary: string;
      leadInspectorSign: string;
    }
  ) => {
    return apiRequest<InspectionReport>(`/inspections/${inspectionId}/report`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approveReport: (inspectionId: string, supervisorId: string = 'QUALITY_SUPERVISOR') => {
    return apiRequest<InspectionReport>(`/inspections/${inspectionId}/report/approve`, {
      method: 'POST',
      body: JSON.stringify({ supervisorId }),
    });
  },
};
